import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiAlertCircle, FiChevronDown, FiChevronRight, FiDownload, FiExternalLink, FiInfo } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { confirmDiscardChanges } from '../../../lib/unsavedChanges';
import { getAccountOwnerId } from '../utils/ownerEntities';
import BusinessPickerTiles, { CoaSetupEntity, getCoaSetupEntities } from './BusinessPickerTiles';
import { taxCodeOf } from '../utils/salesTaxApplicable';
import { buildPeriods, glMovement, returnCsv, returnFromDocuments, ReturnLine } from '../utils/salesTaxReturn';
import './GLChartOfAccountsTab.css';
import './TaxesMasterTab.css';
import { effectiveDocuTypes } from '../utils/docKind';

// =============================================
// Reports → Tax Reports — the sales-tax return worksheet.
// Pick whose books, one of their sales-tax registrations (Masters → Taxes) and a filing
// period: tax charged on sales, tax paid on purchases, and what is owing or refundable,
// from the filed documents of the period — with the registration's GL accounts beside it
// as a check. The sums are utils/salesTaxReturn (tested). Read-only; nothing is posted.
// =============================================
type Row = Record<string, any>;

interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

const today = () => new Date().toISOString().slice(0, 10);
const active = (r: Row) => Number(r.is_deleted) !== 1;
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TaxReportsTab: React.FC<Props> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [data, setData] = useState<Record<string, Row[]>>({});
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [regId, setRegId] = useState<string>('');
  const [periodIdx, setPeriodIdx] = useState(0);
  const [openSection, setOpenSection] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const names = [
        'users', 'user_businesses', 'acctg_salestax_registration', 'acctg_salestax_type', 'acctg_salestax_rate',
        'site_mstr_states', 'site_mstr_countries', 'dms_documents', 'acctg_docu_type',
        'acctg_gl_journal', 'acctg_gl_journal_line', 'acctg_coa',
      ];
      try {
        const rows = await Promise.all(names.map(n => loadTable(n).catch(() => getTableData(n))));
        if (cancelled) return;
        setData(Object.fromEntries(names.map((n, i) => [n, rows[i] || []])));
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || 'Could not load the tax data.');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const t = useCallback((name: string) => data[name] || [], [data]);
  const accountOwnerId = useMemo(() => getAccountOwnerId(t('users')) ?? 0, [t]);
  const entities = useMemo<CoaSetupEntity[]>(
    () => getCoaSetupEntities(viewMode, accountOwnerId, selectedClientId, t('users'), t('user_businesses')),
    [viewMode, accountOwnerId, selectedClientId, t]);

  const regsOf = useCallback((e: CoaSetupEntity) => t('acctg_salestax_registration')
    .filter(r => active(r) && r.owner_type === e.owner_type && Number(r.owner_id) === e.owner_id), [t]);

  // Land on the first person / business that has a registration.
  useEffect(() => {
    if (selectedKey != null || !entities.length) return;
    const first = entities.find(e => regsOf(e).length) || (entities.length === 1 ? entities[0] : null);
    if (first) setSelectedKey(first.key);
  }, [entities, selectedKey, regsOf]);

  const selected = useMemo(() => entities.find(e => e.key === selectedKey) || null, [entities, selectedKey]);
  const regs = useMemo(() => (selected ? regsOf(selected) : []), [selected, regsOf]);

  useEffect(() => {
    if (regs.length && !regs.some(r => String(r.registration_id) === regId)) {
      setRegId(String(regs[0].registration_id));
      setPeriodIdx(0);
    }
    if (!regs.length && regId) setRegId('');
  }, [regs, regId]);
  const reg = regs.find(r => String(r.registration_id) === regId) || null;

  const statesById = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of t('site_mstr_states')) m.set(Number(s.state_id), String(s.state_code || '').toUpperCase());
    return m;
  }, [t]);
  const regLabel = useCallback((r: Row) => {
    const code = taxCodeOf(t('acctg_salestax_type'), r.tax_type_id);
    const sc = r.state_id != null && r.state_id !== '' ? statesById.get(Number(r.state_id)) || '' : '';
    return [code, sc].filter(Boolean).join(' ') + (r.registration_number ? ` · ${r.registration_number}` : '');
  }, [t, statesById]);

  const isCanada = useMemo(() => {
    if (!reg?.country_id) return false;
    const c = t('site_mstr_countries').find(x => Number(x.countries_id) === Number(reg.country_id));
    return String(c?.iso2 || '').toUpperCase() === 'CA';
  }, [reg, t]);

  const periods = useMemo(() => (reg ? buildPeriods({
    frequency: reg.filing_frequency, fiscalYearEnd: reg.fiscal_year_end,
    registeredFrom: reg.registered_from, registeredTo: reg.registered_to, today: today(),
  }) : []), [reg]);
  const period = periods[Math.min(periodIdx, Math.max(0, periods.length - 1))] || null;

  const result = useMemo(() => {
    if (!reg || !period) return null;
    const docs = t('dms_documents').filter(d => Number(d.account_owner_id) === accountOwnerId);
    return returnFromDocuments({
      documents: docs, docuTypes: effectiveDocuTypes(t('acctg_docu_type'), accountOwnerId), rates: t('acctg_salestax_rate'), registration: reg, period,
    });
  }, [reg, period, t, accountOwnerId]);

  const gl = useMemo(() => {
    if (!reg || !period) return { charged: null as number | null, paid: null as number | null };
    const journals = t('acctg_gl_journal').filter(j =>
      Number(j.account_owner_id) === accountOwnerId && j.owner_type === reg.owner_type && Number(j.owner_id) === Number(reg.owner_id));
    const lines = t('acctg_gl_journal_line');
    return {
      charged: glMovement({ coaId: reg.collected_coa_id, journals, lines, period, side: 'credit' }),
      paid: glMovement({ coaId: reg.paid_coa_id, journals, lines, period, side: 'debit' }),
    };
  }, [reg, period, t, accountOwnerId]);

  const coaName = (id: any) => {
    const a = t('acctg_coa').find(x => Number(x.coa_id) === Number(id));
    return a ? `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}` : '';
  };

  const pickEntity = (key: string) => { setSelectedKey(key); setRegId(''); setPeriodIdx(0); setOpenSection({}); };

  const goToTaxesMaster = async () => {
    if (!(await confirmDiscardChanges())) return;
    window.location.hash = 'taxes';
  };

  const downloadCsv = () => {
    if (!result || !reg || !period || !selected) return;
    const heading = `${selected.display_name} — ${regLabel(reg)} — ${period.start} to ${period.end}`;
    const blob = new Blob([returnCsv(result, heading)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-tax_${regLabel(reg).split(' · ')[0].replace(/\s+/g, '-')}_${period.start}_${period.end}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const tileMeta = useCallback((e: CoaSetupEntity) => {
    const n = regsOf(e).length;
    return n ? `${n} tax registration${n === 1 ? '' : 's'}` : 'No tax registration';
  }, [regsOf]);

  if (!loaded) return <div className="coa-tab"><div className="coa-empty"><p>Loading tax reports…</p></div></div>;
  if (viewMode === 'clients' && !selectedClientId) {
    return <div className="coa-tab"><div className="coa-empty"><p>Select a client first.</p></div></div>;
  }

  const Section: React.FC<{ id: string; title: string; lines: ReturnLine[]; total?: number }> = ({ id, title, lines, total }) => {
    const open = !!openSection[id];
    return (
      <div className="tx-section">
        <button type="button" className="tx-section-toggle" aria-expanded={open}
          onClick={() => setOpenSection(s => ({ ...s, [id]: !open }))}>
          {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
          {title} <span className="tx-muted">({lines.length} document{lines.length === 1 ? '' : 's'}{total != null ? ` · ${money(total)}` : ''})</span>
        </button>
        {open && (lines.length === 0 ? <p className="tx-muted">None in this period.</p> : (
          <div className="tx-table-wrap">
            <table className="tx-table">
              <thead><tr><th>Date</th><th>Party</th><th>Document type</th><th className="tx-num">Total</th><th className="tx-num">Tax</th><th /></tr></thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.document_id}>
                    <td>{l.date}</td>
                    <td>{l.party || <span className="tx-muted">—</span>}</td>
                    <td>{l.docType}</td>
                    <td className="tx-num">{l.total != null ? money(l.total) : '—'}</td>
                    <td className="tx-num">{money(l.tax)}{l.estimated && <span className="tx-badge" title="No tax amount on the document — worked out of its total and rate">est.</span>}</td>
                    <td><a className="tx-link" href={`/doc-view?doc=${l.document_id}`} target="_blank" rel="noreferrer" title="Open the document"><FiExternalLink size={13} /></a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="coa-tab tx-tab">
      <div className="coa-info-banner">
        A worksheet for a <strong>sales-tax return</strong>: the tax charged on sales and the tax paid on
        purchases in a filing period, from the documents filed for it. Use it to fill in the return — nothing here is posted or sent.
      </div>
      {loadError && <div className="coa-field-error"><FiAlertCircle size={14} /> {loadError}</div>}

      {entities.length === 0 ? (
        <div className="coa-empty"><p>Nobody found for this {viewMode === 'clients' ? 'client' : 'account'}. Add a person or a business in <strong>Family &amp; Businesses</strong> first.</p></div>
      ) : (
        <>
          {entities.length > 1 && (
            <BusinessPickerTiles entities={entities} selectedKey={selectedKey} onSelect={pickEntity}
              typeName={() => undefined} meta={tileMeta} />
          )}

          {selected && (
            <div className="coa-form-card tx-owner-card">
              <div className="tx-owner-head"><h3>{selected.display_name}</h3></div>

              {regs.length === 0 ? (
                <p className="tx-muted">
                  No sales-tax registration recorded for {selected.display_name}.{' '}
                  <button type="button" className="tx-link" onClick={goToTaxesMaster}>Add one in Masters → Taxes</button>
                </p>
              ) : (
                <>
                  <div className="tx-filters">
                    <label className="coa-field-label" htmlFor="tx-reg">Registration</label>
                    <select id="tx-reg" className="coa-search-input" value={regId}
                      onChange={e => { setRegId(e.target.value); setPeriodIdx(0); setOpenSection({}); }}>
                      {regs.map(r => <option key={r.registration_id} value={r.registration_id}>{regLabel(r)}</option>)}
                    </select>
                    <label className="coa-field-label" htmlFor="tx-period">Period</label>
                    <select id="tx-period" className="coa-search-input" value={periodIdx}
                      onChange={e => { setPeriodIdx(Number(e.target.value)); setOpenSection({}); }}>
                      {periods.map((p, i) => <option key={p.start} value={i}>{p.label}{p.open ? ' (in progress)' : ''}</option>)}
                    </select>
                    {result && (
                      <button type="button" className="coa-btn-secondary tx-btn-sm" onClick={downloadCsv}>
                        <FiDownload size={14} /> CSV
                      </button>
                    )}
                  </div>

                  {!period || !result ? (
                    <p className="tx-muted">No filing period yet — check the registration's dates in Masters → Taxes.</p>
                  ) : (
                    <>
                      {reg?.reporting_method === 'simplified' && (
                        <div className="tx-note"><FiInfo size={14} />
                          This registration uses the {isCanada ? 'Quick Method' : 'simplified / flat-rate method'}: the amount to
                          remit is worked out from a flat rate of sales, not from the figures below. They are shown for reference only.
                        </div>
                      )}
                      {period.open && (
                        <div className="tx-note"><FiInfo size={14} /> This period has not ended yet — the figures are so far.</div>
                      )}

                      <div className="tx-summary">
                        <div className="tx-card">
                          <span className="tx-card-label">Tax charged on sales</span>
                          <span className="tx-card-value">{money(result.charged)}</span>
                          {gl.charged != null && <span className="tx-card-sub" title={coaName(reg?.collected_coa_id)}>Books: {money(gl.charged)}</span>}
                        </div>
                        <div className="tx-card">
                          <span className="tx-card-label">Tax paid on purchases</span>
                          <span className="tx-card-value">{money(result.paid)}</span>
                          {gl.paid != null && <span className="tx-card-sub" title={coaName(reg?.paid_coa_id)}>Books: {money(gl.paid)}</span>}
                        </div>
                        <div className={`tx-card ${result.net >= 0 ? 'tx-card-owing' : 'tx-card-refund'}`}>
                          <span className="tx-card-label">{result.net >= 0 ? 'Net tax owing' : 'Net refund'}</span>
                          <span className="tx-card-value">{money(Math.abs(result.net))}</span>
                          <span className="tx-card-sub">charged − paid</span>
                        </div>
                      </div>

                      {(gl.charged != null || gl.paid != null) && (
                        <p className="tx-muted tx-gl-note">
                          "Books" is the movement of the registration's GL account{gl.charged != null && gl.paid != null ? 's' : ''} in
                          the period, from posted journal entries. A difference means documents and journal entries don't agree yet.
                        </p>
                      )}

                      {result.pendingCount > 0 && (
                        <div className="tx-note tx-note-warn"><FiAlertCircle size={14} />
                          {result.pendingCount} document{result.pendingCount === 1 ? '' : 's'} in this period with this tax {result.pendingCount === 1 ? 'is' : 'are'} not
                          filed yet and {result.pendingCount === 1 ? 'is' : 'are'} not counted. File {result.pendingCount === 1 ? 'it' : 'them'} in Process Docs / Receipts to include {result.pendingCount === 1 ? 'it' : 'them'}.
                        </div>
                      )}

                      <Section id="charged" title="Tax charged — sales invoices, less credit memos and refunds issued" lines={result.chargedLines} total={result.charged} />
                      <Section id="paid" title="Tax paid — receipts, purchase invoices and bills, less credits and refunds received" lines={result.paidLines} total={result.paid} />
                      {result.notCounted.length > 0 && (
                        <Section id="other" title="Not counted — payments, statements, quotes and other documents with tax on them" lines={result.notCounted} />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaxReportsTab;
